import React from "react";
import { lighten, makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";
import {
  Toolbar,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  AppBar,
  Tooltip,
  Button,
  IconButton,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import SearchIcon from "@material-ui/icons/Search";
import ClearAllIcon from "@material-ui/icons/ClearAll";

const useToolbarStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },

  toolbarMobile: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },

  title: {
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },

  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: lighten(theme.palette.common.white, 0.15),
    "&:hover": {
      backgroundColor: lighten(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(1),
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(1),
      width: "auto",
    },
  },

  secondSession: {
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row-reverse",
    },
  },

  clientTotalDiv: {
    background: "#fafafa",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "row",
    lineHeight: "0.78em",
    fontSize: "12px",
    alignItems: "flex-end",
    margin: "10px",

    "& span": {
      marginRight: "1em",
      backgroundColor: lighten(theme.palette.info.light, 0.7),
      padding: "10px 6px",
      borderRadius: 4,
    },

    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      marginBottom: "6px",
      lineHeight: "1.4em",
      fontSize: 10,
    },
  },

  sectionDesktop: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
    },
  },

  sectionMobile: {
    display: "flex",
    flexDirection: "row",
    paddingTop: 2,
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();

  const {
    title,
    handleChange,
    handleChangeAutoComplete,
    values,
    isClients,
    handleInputChange,
    handleFromDateChange,
    handleToDateChange,
    searchHandler,
    clearSearchHandler,
    totalAmount,
    totalVat,
    count,
  } = props;

  const grandTotal = (totalAmount || 0) + (totalVat || 0);

  return (
    <div className={classes.grow}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar className={classes.toolbarMobile}>
          <Tooltip title={title}>
            <Typography className={classes.title} variant="h6" noWrap>
              {title}
            </Typography>
          </Tooltip>

          {/* Company */}
          <div className={classes.search}>
            <Autocomplete
              options={isClients}
              getOptionLabel={(option) => option?.companyName || ""}
              onChange={handleChangeAutoComplete("company")}
              value={values.company || ""}
              style={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  size="small"
                  variant="outlined"
                  onChange={handleInputChange}
                />
              )}
            />
          </div>

          <div className={classes.grow} />

          {/* Desktop Section */}
          <div className={classes.sectionDesktop}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.businessType === "Private"}
                  onChange={handleChange}
                  name="businessType"
                  color="primary"
                />
              }
              label="Private"
            />

            <KeyboardDatePicker
              autoOk
              size="small"
              format="dd/MM/yyyy"
              variant="inline"
              inputVariant="outlined"
              label="From"
              onChange={handleFromDateChange}
              value={values.fromDate}
            />

            <KeyboardDatePicker
              autoOk
              size="small"
              format="dd/MM/yyyy"
              variant="inline"
              inputVariant="outlined"
              label="To"
              onChange={handleToDateChange}
              value={values.toDate}
            />

            <div style={{ display: "flex", alignItems: "center" }}>
              &nbsp;
              <Button
                size="small"
                endIcon={<SearchIcon />}
                onClick={searchHandler}
              >
                Search
              </Button>
              {values.isSearch && (
                <Tooltip title="Clear search">
                  <IconButton onClick={clearSearchHandler}>
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        </Toolbar>
      </AppBar>

      {/* Totals Section */}
      <div className={classes.secondSession}>
        <div className={classes.clientTotalDiv}>
          <span>
            <strong>Total Amount:</strong>{" "}
            {totalAmount ? totalAmount.toFixed(2) : "0.00"}
          </span>

          <span>
            <strong>VAT:</strong> {totalVat ? totalVat.toFixed(2) : "0.00"}
          </span>

          <span>
            <strong>Grand Total:</strong> {grandTotal.toFixed(2)}
          </span>

          <span>
            <strong>Count:</strong> {count || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(EnhancedTableToolbar);
